import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  Collapse,
  HStack,
  Input,
  VStack,
  useColorModeValue,
  useDisclosure
} from "@chakra-ui/react"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import { Field, Form, Formik, useFormikContext } from "formik"
import { useEffect, useState } from "react"
import { StringParam, useQueryParam } from "use-query-params"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import Pathicon from "../pathicon/Pathicon"

const prefix = "order.discount_form"
const translationKeys = {
  placeholder: `${prefix}.placeholder`,
  submit: `${prefix}.submit`,
  invalid: `${prefix}.invalid`,
  discount: `${prefix}.discount`,
  clear: `${prefix}.clear`
}

const schema = z.object({
  code: z.string().min(1)
})
const validate = toFormikValidate(schema)

function FormSubmitter() {
  const { initialValues, handleSubmit } = useFormikContext<{ code: string }>()

  useEffect(() => {
    // on initial mount, let's query for the discount if there is one pulled from the URL search params
    if (initialValues.code) {
      handleSubmit()
    }
  }, [])

  return null
}

type DiscountResult = {
  discount_percent: number
  discount_perc?: number
  currency_display_discount_price: string
  discounted_total_currency_display?: string
  discountMessage?: string
}

export type OrderDiscountFormProps = {
  applyDiscountCode: (code: string) => Promise<DiscountResult>
  clearDiscountCode?: () => void
  searchParamKey?: string
}

function OrderDiscountForm({
  applyDiscountCode,
  searchParamKey,
  clearDiscountCode
}: OrderDiscountFormProps) {
  const colorScheme = useColorModeValue("brand", undefined)
  const { t } = useTranslate()
  const [discountCode] = useQueryParam(searchParamKey || "", StringParam)
  const [successfulDiscountCode, setSuccessfulDiscountCode] = useState<
    string | null
  >(null)
  const [discountResult, setDiscountResult] = useState<DiscountResult | null>(
    null
  )
  const [discountError, setDiscountError] = useState<string | null>(null)

  const { isOpen, onClose, getButtonProps, getDisclosureProps } = useDisclosure(
    {
      defaultIsOpen: true
    }
  )

  useEffect(() => {
    if (isOpen) {
      setDiscountError(null)
      setDiscountResult(null)
      clearDiscountCode?.()
    }
  }, [isOpen])

  async function handleDiscount(discountCode: string) {
    setDiscountError(null)
    setSuccessfulDiscountCode(null)
    try {
      const discountResult = await applyDiscountCode(discountCode)
      if (discountResult) {
        setDiscountResult(discountResult)
        setSuccessfulDiscountCode(discountCode)
        onClose()
      } else {
        throw new Error(
          t(`${translationKeys.invalid}.The code "{{ code }}" is not valid.`, {
            code: discountCode
          })
        )
      }
    } catch (error) {
      setDiscountError((error as unknown as Error).message)
    }
  }

  const discountPercent =
    discountResult?.discount_percent || discountResult?.discount_perc
  const discountDisplayPrice =
    discountResult?.currency_display_discount_price ||
    discountResult?.discounted_total_currency_display

  // Derived discount message from discount result.
  const discountMessage =
    discountPercent && discountDisplayPrice
      ? `${t(
          `${translationKeys.discount}.Discount applied ({{ perc }}% off). Total after discount: {{ total }}`,
          {
            perc: discountPercent * 100,
            total: discountDisplayPrice,
            // For deprecated "payments.elements_form.discount_applied" translation.
            code: successfulDiscountCode,
            newTotal: discountDisplayPrice
          }
        )}.`
      : null

  const discountSuccess = discountResult
    ? typeof discountResult === "string"
      ? discountResult
      : discountMessage || discountResult.discountMessage || null
    : null

  return (
    <VStack w="100%">
      <Collapse
        in={isOpen}
        animateOpacity
        {...getDisclosureProps()}
        style={{
          width: "100%",
          // Note: forcing overflow visible to ensure input outline isn't clipped.
          // The discount form's use of Collapse doesn't appear to necessitate overflow: hidden.
          overflow: "visible"
        }}
      >
        <Formik
          initialValues={{ code: discountCode || "" }}
          onSubmit={({ code }) => handleDiscount(code)}
          validate={validate}
          validateOnMount
          enableReinitialize
        >
          {(form) => (
            <Form style={{ width: "100%" }}>
              <FormSubmitter />
              <HStack w="100%">
                <Field
                  as={Input}
                  name="code"
                  placeholder={t(
                    `${translationKeys.placeholder}.Have a discount code?`
                  )}
                  flexGrow={1}
                  autoFocus
                  borderRadius="40px"
                />

                <Button
                  colorScheme={colorScheme}
                  type="submit"
                  isDisabled={!form.isValid || form.isSubmitting}
                  isLoading={form.isSubmitting}
                  cursor="pointer"
                >
                  {t(`${translationKeys.submit}.Apply Code`)}
                </Button>
              </HStack>
            </Form>
          )}
        </Formik>
      </Collapse>
      {!!discountError && (
        <Collapse in animateOpacity style={{ width: "100%" }}>
          <Alert status="error" variant="subtle" wordBreak="break-word">
            <Pathicon icon="caution-octagon" mr={4} />
            <Box>
              <AlertTitle>{discountError}</AlertTitle>
            </Box>
          </Alert>
        </Collapse>
      )}
      {discountSuccess ? (
        <Collapse in animateOpacity style={{ width: "100%" }}>
          <Alert status="success" variant="subtle" wordBreak="break-word">
            <Pathicon icon="check-square" mr={4} />
            <Box>
              <AlertTitle>{discountSuccess}</AlertTitle>
              <AlertDescription>
                <Button
                  colorScheme={colorScheme}
                  variant="link"
                  mr="auto"
                  cursor="pointer"
                  bg="transparent"
                  {...getButtonProps()}
                >
                  {t(`${translationKeys.clear}.Try a different code?`)}
                </Button>
              </AlertDescription>
            </Box>
          </Alert>
        </Collapse>
      ) : null}
    </VStack>
  )
}

OrderDiscountForm.displayName = "OrderDiscountForm"

OrderDiscountForm.defaultProps = {
  searchParamKey: "coupon"
}

export default OrderDiscountForm
